import { toast } from 'vue-sonner';

export const SCOPE_TOAST_CLASSES = {
    toast: 'scope-toast',
    title: 'scope-toast__title',
    description: 'scope-toast__description',
    actionButton: 'scope-toast__action',
    closeButton: 'scope-toast__close',
    icon: 'scope-toast__icon'
};

export const SCOPE_TOASTER_TOAST_OPTIONS = {
    unstyled: true,
    classes: SCOPE_TOAST_CLASSES
};

export interface ToastOptions {
    type: 'success' | 'error' | 'warning' | 'info';
    duration?: number;
    message?: string;
    title?: string;
    action?: { label: string; callback: () => void };
}

export const DEFAULT_TOAST_OPTIONS: ToastOptions = {
    type: 'info',
    duration: 5000
};

export type DismissFunc = () => void;
export type ToastDismissHandler = { dismiss: DismissFunc } | undefined;

const open = (options: Partial<ToastOptions>): ToastDismissHandler => {
    if (!options?.message) {
        return;
    }

    const props: ToastOptions = { ...DEFAULT_TOAST_OPTIONS, ...options };
    const content = props.title
        ? {
              description: props.message
          }
        : {};

    const action = props.action
        ? {
              label: props.action.label,
              onClick: props.action.callback
          }
        : undefined;
    const closeButton = !props.action;
    const toastTitle = props.title ?? props.message;
    if (!toastTitle) {
        return;
    }

    let id: string | number;
    if (props.type === 'success') {
        id = toast.success(toastTitle, {
            ...SCOPE_TOASTER_TOAST_OPTIONS,
            ...content,
            action,
            closeButton,
            duration: props.duration
        });
    } else if (props.type === 'error') {
        id = toast.error(toastTitle, {
            ...SCOPE_TOASTER_TOAST_OPTIONS,
            ...content,
            action,
            closeButton,
            duration: props.duration
        });
    } else if (props.type === 'warning') {
        id = toast.warning(toastTitle, {
            ...SCOPE_TOASTER_TOAST_OPTIONS,
            ...content,
            action,
            closeButton,
            duration: props.duration
        });
    } else {
        id = toast.info(toastTitle, {
            ...SCOPE_TOASTER_TOAST_OPTIONS,
            ...content,
            action,
            closeButton,
            duration: props.duration
        });
    }

    const dismiss = () => toast.dismiss(id);

    return {
        dismiss
    };
};

const success = (
    message: string,
    options?: Pick<ToastOptions, 'duration' | 'action' | 'title'>
) => {
    return open({
        ...options,
        message,
        type: 'success'
    });
};

const error = (
    message: string,
    options?: Pick<ToastOptions, 'duration' | 'action' | 'title'>
) => {
    return open({
        ...options,
        message,
        type: 'error'
    });
};

const info = (
    message: string,
    options?: Pick<ToastOptions, 'duration' | 'action' | 'title'>
) => {
    return open({
        ...options,
        message,
        type: 'info'
    });
};

const warning = (
    message: string,
    options?: Pick<ToastOptions, 'duration' | 'action' | 'title'>
) => {
    return open({
        ...options,
        message,
        type: 'warning'
    });
};

export const useToastNew = () => {
    return {
        open,
        success,
        error,
        info,
        warning
    };
};
